// src/components/tasks/MetadataRow.tsx

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { LucideIcon } from 'lucide-react';
import { Edit } from '../common/icons';

/**
 * Metadata 行組件的 Props
 */
interface MetadataRowProps {
  /**
   * 圖示組件（來自 lucide-react）
   */
  icon: LucideIcon;

  /**
   * 標籤文字
   */
  label: string;

  /**
   * 顯示值（可選）
   * 如果未提供，該行不會渲染（向後兼容）
   */
  value?: string | number;

  /**
   * Accent 顏色（可選）
   * - 'blue': 特殊標記（如連結）
   * - 'orange': 緊迫日期（7天內）
   * - 'red-muted': 過期日期（低調紅）
   * - 'default': 預設灰色（不緊迫或已關閉）
   */
  accentColor?: 'blue' | 'orange' | 'red-muted' | 'default';

  /**
   * 編輯回調（可選）
   * 提供時會顯示編輯按鈕
   */
  onEdit?: () => void;

  /**
   * 點擊回調（可選）
   * 提供時整列都可點擊，用於開啟日期選擇器等
   * 優先級低於 onEdit（如果同時提供，點擊編輯按鈕會觸發 onEdit）
   */
  onClick?: () => void;

  /**
   * 行動版顯示 Label（可選）
   * 當為 true 時，在行動版會顯示 Icon | Label | Value
   * 桌面版則只顯示 Icon | Value（Label 會被隱藏）
   */
  showLabelOnMobile?: boolean;

  /**
   * Inline 編輯模式（可選）
   * 提供時，點擊 value 區域會變成輸入框，失焦自動儲存
   */
  inlineEdit?: {
    onSave: (value: string | null) => Promise<void>;
    placeholder?: string;
    maxLength?: number;
  };

  /**
   * Inline 循環選擇模式（可選）
   * 點擊循環切換選項，適用於少量有序選項 (2-4個)
   *
   * 互動方式：
   * - 單擊：循環切換到下一個選項
   *   - allowClear=true: None → Option1 → Option2 → ... → None
   *   - allowClear=false: Option1 → Option2 → ... → Option1
   * - 雙擊：清空值（僅當 allowClear=true）
   *
   * 注意：currentValue 是實際儲存的值（如 "1"），會透過 options 找到對應的 label 顯示
   */
  inlineCycle?: {
    currentValue: string | undefined;  // 實際值（用於比對和循環）
    options: Array<{
      value: string;
      label: string;
      color?: 'red' | 'yellow' | 'blue' | 'green' | 'purple' | 'pink' | 'gray';
    }>;
    onSelect: (value: string | undefined) => void;  // 同步呼叫，由上層處理 debounce
    allowClear?: boolean;  // 是否允許清空值（預設 true）
  };
}

/**
 * MetadataRow - 統一的 metadata 顯示組件
 *
 * 用於 TaskDetail 右側欄的 metadata 顯示
 *
 * @example
 * ```tsx
 * <MetadataRow
 *   icon={CalendarClock}
 *   label="Plan"
 *   value="This Week"
 *   accentColor="blue"
 *   onEdit={() => setShowPlanSelector(true)}
 * />
 * ```
 */
const MetadataRow: React.FC<MetadataRowProps> = ({
  icon: Icon,
  label,
  value,
  accentColor = 'default',
  onEdit,
  onClick,
  showLabelOnMobile = false,
  inlineEdit,
  inlineCycle
}) => {
  // Inline editing 狀態
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(String(value || ''));
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Inline cycle 狀態（用於雙擊檢測）
  const lastClickTimeRef = useRef<number>(0);

  // 同步外部 value 變化
  useEffect(() => {
    if (!isEditing) {
      setEditValue(String(value || ''));
    }
  }, [value, isEditing]);

  // 進入編輯模式時自動聚焦
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      const length = inputRef.current.value.length;
      inputRef.current.setSelectionRange(length, length);
    }
  }, [isEditing]);

  // 進入編輯模式
  const handleStartEdit = useCallback(() => {
    if (inlineEdit && !isSaving) {
      setIsEditing(true);
    }
  }, [inlineEdit, isSaving]);

  // 儲存變更
  const handleSave = useCallback(async () => {
    if (!inlineEdit) return;

    const trimmedValue = editValue.trim();
    const originalValue = String(value || '');

    // 若值沒有變更，直接退出編輯模式
    if (trimmedValue === originalValue) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await inlineEdit.onSave(trimmedValue || null);
      setIsEditing(false);
    } catch {
      // 儲存失敗時恢復原值
      setEditValue(originalValue);
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  }, [editValue, value, inlineEdit]);

  // 取消編輯
  const handleCancel = useCallback(() => {
    setEditValue(String(value || ''));
    setIsEditing(false);
  }, [value]);

  // 處理按鍵事件
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    } else if (e.key === 'Enter') {
      if (e.nativeEvent.isComposing) return;
      e.preventDefault();
      handleSave();
    }
  }, [handleCancel, handleSave]);

  // Inline cycle 處理 - 點擊循環切換（樂觀更新，不阻擋連續點擊）
  const handleCycleClick = useCallback(() => {
    if (!inlineCycle) return;

    const now = Date.now();
    const timeSinceLastClick = now - lastClickTimeRef.current;
    lastClickTimeRef.current = now;

    const allowClear = inlineCycle.allowClear ?? true; // 預設允許清空

    // 雙擊檢測（300ms 內連續點擊視為雙擊）
    const isDoubleClick = timeSinceLastClick < 300;

    const currentValue = inlineCycle.currentValue;
    let newValue: string | undefined;

    if (isDoubleClick && allowClear) {
      // 雙擊：清空值（僅當允許清空時）
      newValue = undefined;
    } else {
      // 單擊：循環到下一個選項
      const currentIndex = inlineCycle.options.findIndex(opt => opt.value === currentValue);
      if (currentIndex === -1) {
        // 當前無值，選擇第一個選項
        newValue = inlineCycle.options[0]?.value;
      } else if (currentIndex === inlineCycle.options.length - 1) {
        // 已是最後一個
        if (allowClear) {
          // 允許清空：循環回 undefined
          newValue = undefined;
        } else {
          // 不允許清空：循環回第一個選項
          newValue = inlineCycle.options[0]?.value;
        }
      } else {
        // 選擇下一個選項
        newValue = inlineCycle.options[currentIndex + 1]?.value;
      }
    }

    // 如果值沒有變化，不需要更新
    if (newValue === currentValue) return;

    // 樂觀更新：立即呼叫 onSelect，不等待結果（由上層處理 debounce）
    inlineCycle.onSelect(newValue);
  }, [inlineCycle]);

  // Accent 顏色類名映射
  const accentColorClasses = {
    blue: 'text-sky-600',
    orange: 'text-orange-600',
    'red-muted': 'text-red-400',  // 低調紅色（過期日期）
    default: 'text-gray-600'
  };

  const iconColorClass = accentColorClasses[accentColor];

  // 判斷是否有值
  const hasValue = value !== undefined && value !== null && value !== '';

  // Value 的 accent 類名
  const valueAccentClass = accentColor === 'blue' ? 'accent-blue' :
                           accentColor === 'orange' ? 'accent-orange' : '';

  // 判斷是否可點擊（onClick 或 inlineEdit 或 inlineCycle）
  const isClickable = !!onClick || !!inlineEdit || !!inlineCycle;

  // 渲染 inline editing 輸入框
  if (isEditing && inlineEdit) {
    return (
      <div className="meta-row">
        <Icon className={`w-[18px] h-[18px] shrink-0 ${hasValue ? iconColorClass : 'text-gray-400'}`} />
        {showLabelOnMobile && (
          <span className="md:hidden w-24 text-sm text-gray-600 font-normal text-left shrink-0">
            {label}
          </span>
        )}
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleSave}
          maxLength={inlineEdit.maxLength}
          placeholder={inlineEdit.placeholder || label}
          disabled={isSaving}
          className="flex-1 px-2 py-0.5 text-sm border border-sky-400 rounded
                     focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-sky-500
                     disabled:opacity-50"
        />
      </div>
    );
  }

  // 點擊區域的內容
  const content = (
    <>
      {hasValue ? (
        <>
          {/* 有值：Icon + Label (行動版) + Value */}
          <Icon className={`w-[18px] h-[18px] shrink-0 ${iconColorClass}`} />

          {/* 行動版顯示 Label（桌面版隱藏）*/}
          {showLabelOnMobile && (
            <span className="md:hidden w-24 text-sm text-gray-600 font-normal text-left shrink-0">
              {label}
            </span>
          )}

          <span
            className={`meta-value ${valueAccentClass}`}
            title={inlineCycle ? `${label}: ${value} (click to cycle, double-click to clear)` : `${label}: ${value}`}
          >
            {value}
          </span>
        </>
      ) : (
        <>
          {/* 無值：Icon + Label (placeholder) */}
          <Icon className="w-[18px] h-[18px] shrink-0 text-gray-400" />
          <span className="meta-value empty">{inlineEdit?.placeholder || label}</span>
        </>
      )}
    </>
  );

  // 處理點擊事件
  const handleClick = () => {
    if (inlineCycle) {
      handleCycleClick();
    } else if (inlineEdit) {
      handleStartEdit();
    } else if (onClick) {
      onClick();
    }
  };

  return (
    <div className={`meta-row ${isClickable ? 'cursor-pointer hover:bg-gray-50 -mx-2 px-2 rounded-lg transition-colors' : ''}`}>
      {isClickable ? (
        <div onClick={handleClick} className="flex items-center gap-2.5 flex-1 min-w-0">
          {content}
        </div>
      ) : (
        content
      )}

      {/* 編輯按鈕（Plan 專用，ml-auto 推到最右側）*/}
      {onEdit && (
        <button
          onClick={(e) => {
            e.stopPropagation(); // 防止觸發外層的 onClick
            onEdit();
          }}
          className="shrink-0 p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors ml-auto"
          aria-label={`Edit ${label}`}
        >
          <Edit className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
};

export default MetadataRow;
